import React, { useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { PrintProduct } from "../../../CheckoutPage/components/types";
import DiscountTierPanel from "./DiscountTierPanel";

const TruckIcon = () => (
  <svg width="14" height="12" viewBox="0 0 24 18" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path d="M1 1h14v11H1V1zm14 3h4l3 4v4h-7V4zM5.5 16a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm11 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z" stroke="#30053f" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

interface PrintProductPreviewProps {
  product: PrintProduct;
  variant: "wide" | "compact";
  thumbCenterY: number;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  pinned?: boolean;
  onClose?: () => void;
}

const DISCLAIMER =
  "Final print results may vary depending on image size and dimensions. Cropped images are centered, which may remove edge portions of the photo.";

const BASE_CARD_HEIGHT = 340;
const DISCOUNT_PANEL_HEIGHT = 100;
const FREE_SHIPPING_BANNER_HEIGHT = 50;
const CARD_WIDTH = 420;
const PAD = 16;
const ARROW_HALF = 8;

const PrintProductPreview: React.FC<PrintProductPreviewProps> = ({
  product,
  variant,
  thumbCenterY,
  onMouseEnter,
  onMouseLeave,
  pinned = false,
  onClose
}) => {
  const cardHeight =
    BASE_CARD_HEIGHT +
    (product.discountTiers.length > 0 ? DISCOUNT_PANEL_HEIGHT : 0) +
    (product.freeShipping ? FREE_SHIPPING_BANNER_HEIGHT : 0);

  const [wrapperStyle, setWrapperStyle] = useState<React.CSSProperties>({
    position: "fixed",
    left: -9999,
    top: -9999
  });
  const [arrowOffset, setArrowOffset] = useState<number>(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useLayoutEffect(() => {
    setImageLoaded(false);
    if (imgRef.current?.complete) setImageLoaded(true);
  }, [product.id]);

  useLayoutEffect(() => {
    if (variant === "wide") {
      const photoArea = document.querySelector<HTMLElement>(
        ".lightbox-main-content .modal-body"
      );
      if (!photoArea) return;
      const photoRect = photoArea.getBoundingClientRect();
      const idealTop = thumbCenterY - cardHeight / 2;
      const clampedTop = Math.max(
        PAD,
        Math.min(idealTop, window.innerHeight - PAD - cardHeight)
      );
      setArrowOffset(Math.max(0, Math.min(thumbCenterY - clampedTop - ARROW_HALF, cardHeight - ARROW_HALF * 2)));
      setWrapperStyle({
        position: "fixed",
        width: CARD_WIDTH,
        left: Math.max(PAD, photoRect.right - CARD_WIDTH - PAD),
        top: clampedTop,
        height: cardHeight,
        zIndex: 1060
      });
    } else {
      const cardLeft = Math.max(PAD, (window.innerWidth - CARD_WIDTH) / 2);
      const idealTop = thumbCenterY - cardHeight - PAD;
      const clampedTop = Math.max(PAD, idealTop);
      setArrowOffset(CARD_WIDTH / 2 - ARROW_HALF);
      setWrapperStyle({
        position: "fixed",
        width: CARD_WIDTH,
        left: cardLeft,
        top: clampedTop,
        height: cardHeight,
        zIndex: 1060
      });
    }
  }, [variant, product.id, thumbCenterY, cardHeight]);

  const previewSrc = product.previewImageURL || product.previewImageThumbnailURL;

  return ReactDOM.createPortal(
    <div
      style={wrapperStyle}
      onMouseEnter={onMouseEnter}
      onMouseLeave={pinned ? undefined : onMouseLeave}
    >
      <div
        className={
          variant === "wide"
            ? "print-product-preview-arrow"
            : "print-product-preview-arrow print-product-preview-arrow--down"
        }
        style={variant === "wide" ? { top: arrowOffset } : { left: arrowOffset }}
      />

      <div className="print-product-preview-card">
        <div className="print-product-preview-header">
          <div className="print-product-preview-header-info">
            <p className="print-product-preview-header-title">{product.title}</p>
            <p className="print-product-preview-header-price">{product.totalPriceFormatted}</p>
          </div>
          {onClose && (
            <button
              className="print-product-preview-close-btn"
              onClick={onClose}
              aria-label="Close preview"
            >
              CLOSE
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
                <line x1="0.5" y1="0.5" x2="7.5" y2="7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <line x1="7.5" y1="0.5" x2="0.5" y2="7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>
        {product.freeShipping && (
          <div className="print-zoom-free-shipping-banner print-zoom-free-shipping-banner--preview">
            <TruckIcon />
            <span><strong>Free shipping:</strong> Makes all print products shipping free</span>
          </div>
        )}
        {product.discountTiers.length > 0 && (
          <DiscountTierPanel
            tiers={product.discountTiers}
            className="print-product-preview-discount"
            badgeClassName="print-zoom-discount-badge"
            tiersClassName="print-zoom-discount-tiers"
            highlightClassName="print-zoom-discount-highlight"
          />
        )}
        <div className="print-product-preview-image-wrapper">
          {previewSrc ? (
            <>
              {!imageLoaded && (
                <div className="print-product-preview-spinner">
                  <i className="fa fa-spinner fa-spin" />
                </div>
              )}
              <img
                ref={imgRef}
                src={previewSrc}
                alt={product.title}
                className="print-product-preview-image"
                style={imageLoaded ? undefined : { opacity: 0 }}
                onLoad={() => setImageLoaded(true)}
              />
            </>
          ) : (
            <div className="print-product-preview-image-placeholder">No preview image</div>
          )}
        </div>
        <p className="print-product-preview-disclaimer">
          <strong>Please note: </strong>
          {DISCLAIMER}
        </p>
      </div>
    </div>,
    document.body
  );
};

export default PrintProductPreview;
